import React from "react"
import {
  Box, Text, Image, Flex, Radio, RadioGroup,
} from "@chakra-ui/react"
import { successToast } from "utilities/toasts"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { useController } from "react-hook-form"
import * as Yup from "yup"
import { Website } from "sharedTypes"
import { HeroTemplateFormValues } from "pages/EventShow/WebPage/types"

type Props = {
  eventId: string
  website: Website
  onClose: () => void
  onHide: () => void
  onChange?: (values: HeroTemplateFormValues) => void
}

const HeroTemplate = ({
  eventId, website, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const templateId = website.templateId || null

  const templates = [
    {
      name: "Wide",
      description: "Event image and details shown side by side in a fixed height section, with the rest of the page visible below.",
      url: "https://data.diobox.com/app/web/assets/webpage-templates/thumb-wide.jpg",
      id: 1,
    },
    {
      name: "Full Screen",
      description: "Full screen image with the event title and registration button at the bottom. Additional event details appear below.",
      url: "https://data.diobox.com/app/web/assets/webpage-templates/thumb-full-screen.jpg",
      id: 2,
    },
    {
      name: "Full Screen with Overlay",
      description: "Full screen image with event details and registration button centered over a darkened overlay.",
      url: "https://data.diobox.com/app/web/assets/webpage-templates/thumb-full-screen-overlay.jpg",
      id: 3,
    },
  ]

  const validationSchema = Yup.object().shape({
    templateId: Yup.number().required("Please select a template"),
  })

  const onSubmit = (values: { templateId: number }) => {
    const templateName = templates.find((t) => t.id === values.templateId)?.name

    return api.putWebsite(eventId, { templateId: values.templateId })
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        successToast({ title: `Updated website template to ${templateName}` })
        onHide()
      })
  }

  return (
    <ModalForm
      title="Hero Template"
      size="4xl"
      initialValues={{ templateId: templateId || 1 }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onClose={onClose}
      disableSubmitWhenNotDirty
      onChange={onChange}
    >
      {({ control }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { field } = useController({
          name: "templateId",
          control,
        })

        return (
          <>
            <Text fontSize="lg" mb={8}>
              Select how the main event image, event details, and primary call to action appear at
              the top of your event page.
            </Text>
            <RadioGroup
              value={field.value?.toString()}
              onChange={(value) => field.onChange(parseInt(value, 10))}
            >
              <Flex gap={6} justify="space-evenly" align="flex-start">
                {templates.map((template) => {
                  const selected = field.value === template.id

                  return (
                    <Box
                      key={template.id}
                      flex={1}
                    >
                      <Box
                        borderRadius="lg"
                        mb={2}
                        cursor="pointer"
                        onClick={() => field.onChange(template.id)}
                        bgColor="white"
                        overflow="hidden"
                      >
                        <Image
                          src={template.url}
                          alt={template.name}
                          w="100%"
                          h="auto"
                          objectFit="cover"
                          borderRadius="lg"
                          ignoreFallback
                        />
                      </Box>
                      <Flex
                        align="flex-start"
                        gap={2}
                        justify="flex-start"
                        mb={2}
                        cursor="pointer"
                        onClick={() => field.onChange(template.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            field.onChange(template.id)
                          }
                        }}
                      >
                        <Radio
                          value={template.id.toString()}
                          isChecked={selected}
                          onChange={() => field.onChange(template.id)}
                          mt={1}
                          pointerEvents="none"
                        />
                        <Box flex={1}>
                          <Text fontSize="lg" fontWeight="semibold" mb={1}>
                            {template.name}
                          </Text>
                          <Text fontSize="sm" color="gray.900">
                            {template.description}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  )
                })}
              </Flex>
            </RadioGroup>
          </>
        )
      }}
    </ModalForm>
  )
}

export default HeroTemplate
